import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlassIcon as Search, CircleNotchIcon as Spinner, WarningIcon as Warning, GraphIcon as Graph } from "@phosphor-icons/react";
import { getFieldImpact } from "@/lib/api";
import { IO_KINDS, typeLabel } from "@/lib/fieldDependencyConstants";
import { SCREEN_KINDS } from "@/lib/screenFields";
import ConfidenceBadge from "@/components/fielddeps/ConfidenceBadge";
import TypeBadge from "@/components/fielddeps/TypeBadge";
import ValidationBadge from "@/components/validation/ValidationBadge";

function Block({ title, count, children, testid }) {
  return (
    <div data-testid={testid}>
      <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] mb-1.5">{title} ({count})</div>
      {children}
    </div>
  );
}

/** "Which programs / rules / files touch field X?" — search + result (Iter 41). */
export default function FieldImpactPanel({ workspaceId, field, file, onSearch, onOpenReference }) {
  const [pending, setPending] = useState(field || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { setPending(field || ""); }, [field]);
  useEffect(() => {
    if (!field) { setData(null); setError(null); return; }
    let alive = true;
    setLoading(true);
    setError(null);
    getFieldImpact(workspaceId, { field, file: file || undefined })
      .then((d) => alive && setData(d))
      .catch((e) => alive && setError(e?.response?.data?.detail || "Could not load field impact."))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [workspaceId, field, file]);

  const submit = (e) => {
    e?.preventDefault?.();
    const v = pending.trim().toUpperCase();
    if (v) onSearch(v);
  };

  return (
    <div className="ml-card p-4 space-y-4" data-testid="fd-impact-panel">
      <form onSubmit={submit} className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[200px]">
          <label className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] block mb-1">Field impact search</label>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--ml-text-secondary)]" />
            <input type="text" value={pending} onChange={(e) => setPending(e.target.value)} placeholder="e.g. CUSTNO or YTDAVG"
              className="ml-input pl-8 w-full font-mono uppercase" data-testid="fd-impact-input" />
          </div>
        </div>
        <button type="submit" className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--ml-primary)] text-white text-sm font-medium hover:bg-[var(--ml-primary-hover)] transition-colors" data-testid="fd-impact-btn">
          <Graph size={14} weight="bold" /> Who touches it?
        </button>
      </form>

      {!field ? (
        <p className="text-xs text-[var(--ml-text-secondary)]" data-testid="fd-impact-empty">
          Enter a field name to see the files that contain it (DSPFFD), the programs and business rules that touch it, and the evidence behind each link.
        </p>
      ) : loading ? (
        <div className="text-sm text-[var(--ml-text-secondary)] inline-flex items-center gap-2"><Spinner size={14} className="animate-spin" /> Resolving {field}…</div>
      ) : error ? (
        <div className="text-sm text-[var(--ml-error)] flex items-center gap-2" data-testid="fd-impact-error"><Warning size={14} /> {error}</div>
      ) : data ? (
        <div className="space-y-4" data-testid="fd-impact-result" data-field={data.field_name}>
          <div className="flex flex-wrap items-center gap-3 border-l-2 border-[var(--ml-primary)] bg-[var(--ml-surface)] px-3 py-2 text-xs" data-testid="fd-impact-totals">
            <span className="ml-mono font-semibold text-sm">{data.field_name}</span>
            <span>{data.totals.files} file{data.totals.files === 1 ? "" : "s"}</span>
            <span>{data.totals.programs} program{data.totals.programs === 1 ? "" : "s"}</span>
            <span>{data.totals.business_rules} rule{data.totals.business_rules === 1 ? "" : "s"}</span>
            <span>{data.totals.references} reference{data.totals.references === 1 ? "" : "s"} · {data.totals.high_confidence} high</span>
            <span className="inline-flex flex-wrap gap-1" data-testid="fd-impact-io">
              {IO_KINDS.filter((k) => data.totals.io?.[k]).map((k) => <TypeBadge key={k} type={k} count={data.totals.io[k]} testid={`fd-impact-io-${k}`} />)}
            </span>
            {data.totals.screen && SCREEN_KINDS.some((k) => data.totals.screen[k]) && (
              <span className="inline-flex flex-wrap gap-1" data-testid="fd-impact-screen">
                {SCREEN_KINDS.filter((k) => data.totals.screen[k]).map((k) => <TypeBadge key={k} type={k} count={data.totals.screen[k]} testid={`fd-impact-screen-${k}`} />)}
              </span>
            )}
            {data.ambiguous && (
              <span className="ml-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border" style={{ background: "#fff8e1", color: "#8a6d00", borderColor: "#f1c21b" }} data-testid="fd-impact-ambiguous">
                ambiguous name — {data.totals.files} files
              </span>
            )}
          </div>
          {data.ambiguous && (
            <p className="text-xs text-[var(--ml-text-secondary)]" data-testid="fd-impact-ambiguous-note">
              This field name lives in several files, so exact lineage is not claimed. Narrow by file:
              {" "}
              {data.files.map((f) => (
                <button key={f.field_id} type="button" onClick={() => onSearch(data.field_name, f.file_name)}
                  className="ml-mono text-[var(--ml-primary)] hover:underline mr-2" data-testid={`fd-impact-narrow-${f.file_name}`}>
                  {f.library_name}/{f.file_name}
                </button>
              ))}
            </p>
          )}
          {file && (
            <p className="text-xs text-[var(--ml-text-secondary)]" data-testid="fd-impact-narrowed-note">
              Narrowed to file <span className="ml-mono font-semibold">{file}</span> —{" "}
              <button type="button" onClick={() => onSearch(data.field_name)} className="text-[var(--ml-primary)] hover:underline" data-testid="fd-impact-widen">
                show all files
              </button>
            </p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <Block title="Files containing (DSPFFD)" count={data.files.length} testid="fd-impact-files">
              {data.files.length === 0 ? <p className="text-xs text-[var(--ml-text-secondary)]">Not in the DSPFFD catalog — work field, or DSPFFD not imported.</p> : (
                <ul className="text-xs space-y-1">
                  {data.files.map((f) => (
                    <li key={f.field_id} className="ml-mono flex items-center justify-between gap-2">
                      <span>{f.library_name}/{f.file_name}{f.key_field ? <span className="text-[#0e6027]"> · key</span> : ""}</span>
                      <span className="text-[var(--ml-text-secondary)]">{f.data_type}{f.field_length ? `(${f.field_length}${f.decimal_positions ? `,${f.decimal_positions}` : ""})` : ""}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Block>
            <Block title="Programs touching" count={data.programs.length} testid="fd-impact-programs">
              {data.programs.length === 0 ? <p className="text-xs text-[var(--ml-text-secondary)]">No deterministic program reference.</p> : (
                <ul className="text-xs space-y-1">
                  {data.programs.map((p) => (
                    <li key={p.program_name} className="flex items-center justify-between gap-2" data-testid={`fd-impact-program-${p.program_name}`}>
                      <span className="ml-mono font-semibold min-w-0">{p.program_name}<span className="text-[var(--ml-text-secondary)] font-normal"> · {p.reference_types.map(typeLabel).join(", ")}{p.files.length ? ` · ${p.files.join(", ")}` : ""}</span>
                        {p.io && IO_KINDS.some((k) => p.io[k]) && (
                          <span className="ml-2 inline-flex gap-1 align-middle" data-testid={`fd-impact-program-io-${p.program_name}`}>
                            {IO_KINDS.filter((k) => p.io[k]).map((k) => <TypeBadge key={k} type={k} count={p.io[k]} />)}
                          </span>
                        )}
                      </span>
                      <ConfidenceBadge confidence={p.best_confidence} ambiguous={p.ambiguous} testid={`fd-impact-program-conf-${p.program_name}`} />
                    </li>
                  ))}
                </ul>
              )}
            </Block>
            <Block title="Business rules" count={data.business_rules.length} testid="fd-impact-rules">
              {data.business_rules.length === 0 ? <p className="text-xs text-[var(--ml-text-secondary)]">No extracted rule references this field.</p> : (
                <ul className="text-xs space-y-1">
                  {data.business_rules.map((br) => (
                    <li key={br.id} className="flex items-start justify-between gap-2">
                      <Link to={`/workspaces/${workspaceId}/business-rules?program=${encodeURIComponent(br.program_name)}&field=${encodeURIComponent(data.field_name)}`} className="ml-mono text-[var(--ml-primary)] hover:underline min-w-0 truncate" title={br.condition_text}>
                        {br.program_name}:{br.line_number} {br.rule_kind.toUpperCase()} {br.condition_text}
                      </Link>
                      <ValidationBadge state={br.validation_state} row={br} testid={`fd-impact-rule-state-${br.id}`} />
                    </li>
                  ))}
                </ul>
              )}
            </Block>
            <Block title="Display files exposing (DSPF)" count={(data.display_files || []).length} testid="fd-impact-displays">
              {(data.display_files || []).length === 0 ? <p className="text-xs text-[var(--ml-text-secondary)]">No parsed display file exposes this field (or a REFFLD alias of it).</p> : (
                <ul className="text-xs space-y-1">
                  {data.display_files.map((d) => (
                    <li key={`${d.display_file}-${d.record_format}`} className="flex items-start justify-between gap-2" data-testid={`fd-impact-display-${d.display_file}-${d.record_format}`}>
                      <span className="ml-mono min-w-0">
                        <span className="font-semibold">{d.display_file}</span><span className="text-[var(--ml-text-secondary)]">/{d.record_format || "?"}</span>
                        {d.screen_field !== data.field_name && <span className="text-[var(--ml-text-secondary)]"> as {d.screen_field}</span>}
                        {d.program_name && <span className="text-[var(--ml-text-secondary)]"> · {d.program_name}</span>}
                        <span className="ml-2 inline-flex gap-1 align-middle">{d.roles.map((k) => <TypeBadge key={k} type={k} />)}</span>
                        <span className="block text-[var(--ml-text-secondary)]">{d.input_capable ? "input-capable screen field" : "output/display field"}{d.mapped_file ? ` · mapped to ${d.mapped_file} (REF / REFFLD)` : d.ambiguous ? " · ambiguous database mapping" : " · no database mapping claimed"}</span>
                      </span>
                      <ConfidenceBadge confidence={d.confidence} ambiguous={d.ambiguous} testid={`fd-impact-display-conf-${d.display_file}`} />
                    </li>
                  ))}
                </ul>
              )}
            </Block>
          </div>
          {data.references.length > 0 && (
            <div className="text-xs text-[var(--ml-text-secondary)]">
              {data.references.length} reference row{data.references.length === 1 ? "" : "s"} —{" "}
              <button type="button" onClick={() => onOpenReference(data.references[0].id)} className="text-[var(--ml-primary)] hover:underline" data-testid="fd-impact-open-first-ref">open the first</button>
              {" "}or filter the table below by this field.
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
